import React, { useState } from 'react';
import { Typography } from '@mui/material';
import { Box } from '@mui/system';
import { makeStyles } from 'tss-react/mui';
import palette from '@ui/palette';
import Button from '@ui/Button';
import Icon from '@ui/Icon';
import {
  SuggestionsByRegion,
  useReplacementsDictionaryContext,
} from '@replacements/context/ReplacementsDictionaryContext';
import DictionaryBody from '@replacements/components/ReplacementsDictionary/DictionaryBody';
import SmartSuggestionsBody from '@replacements/components/ReplacementsDictionary/SmartSuggestionsBody';
import { ReplacementSuggestion } from '@replacements/types/replacementsDictionaryTypes';
import {
  REPLACEMENT_TAB_ICONS,
  REPLACEMENT_TABS,
  ReplacementTab,
  useReplacementTab,
} from '@replacements/context/replacementsDictionaryTabs/tabsContext';
import useLlmPronunciationsQuery from '@replacements/hooks/smartReplacements/useLlmPronunciationsQuery';
import { EventName } from 'services/eventTrackingServiceTypes';
import { useEventsContext } from 'context/events/EventsContext';
import Tabs from 'components/Tabs/Tabs';

interface TabbedReplacementsBodyProps {
  wordInput?: string;
  suggestions: SuggestionsByRegion;
  location: string;
  replacementInput?: string;
  onClose: () => void;
}

const useStyles = makeStyles()(theme => ({
  replacementsBodyRoot: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    height: theme.spacing(59),
  },
  tabsHeader: {
    flex: 0,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    height: theme.spacing(6.25),
    backgroundColor: palette.gray_900,
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(2),
  },
  tab: {
    marginRight: 0,
    marginLeft: theme.spacing(1),
  },
  closeButton: {
    height: theme.spacing(3),
    marginLeft: 'auto',
    color: palette.gray_300,
    padding: theme.spacing(0.5),
    borderRadius: theme.spacing(0.5),
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
  footer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(1, 3),
  },
}));

const TabbedReplacementsBody = (props: TabbedReplacementsBodyProps) => {
  const { wordInput, onClose, replacementInput } = props;
  const [eventTracking] = useEventsContext();
  const { handleSelect } = useReplacementsDictionaryContext();
  const { tab, setTab } = useReplacementTab();
  const [llmSuggestions, setLlmSuggestions] = useState<ReplacementSuggestion[]>(
    []
  );
  const [llmSuggestionsRequested, setLlmSuggestionsRequested] =
    useState<boolean>(false);
  const { classes } = useStyles();
  const [getLlmPronunciationSuggestionsMutation, { loading: llmLoading }] =
    useLlmPronunciationsQuery({
      word: wordInput ?? '',
    });

  const onTabChange = async (newTab: ReplacementTab) => {
    setTab(newTab);
    if (
      wordInput &&
      wordInput !== '' &&
      newTab === 'smart suggestions' &&
      !llmSuggestionsRequested
    ) {
      setLlmSuggestionsRequested(true);
      eventTracking.track({ type: EventName.SmartSuggestionsCTAClicked });
      const result = await getLlmPronunciationSuggestionsMutation();
      const mappedSuggestions = (
        result.data?.llmPronunciationSuggestion.suggestions || []
      ).map(suggestion => ({
        id: `llm:${suggestion.pronunciation}`,
        label: suggestion.pronunciation,
        isAdded: false,
        onSelect: () => {
          eventTracking.track({
            type: EventName.SmartSuggestionSelected,
            data: {
              text: wordInput,
              replacement: suggestion.pronunciation,
            },
          });
          handleSelect(suggestion.pronunciation);
        },
      }));
      setLlmSuggestions(mappedSuggestions);
    }
  };

  return (
    <Box className={classes.replacementsBodyRoot}>
      <Box className={classes.tabsHeader}>
        <Tabs
          id={'replacement-suggestions'}
          values={[...REPLACEMENT_TABS]}
          icons={REPLACEMENT_TAB_ICONS}
          onChange={onTabChange}
          tabClassName={classes.tab}
        />
        <Button
          variant="icon"
          aria-label={'close'}
          className={classes.closeButton}
          onClick={onClose}
        >
          <Icon type="ex" />
        </Button>
      </Box>
      {tab === 'oxford' ? (
        <DictionaryBody {...props} displayWordInput={false} />
      ) : (
        <SmartSuggestionsBody
          wordInput={wordInput}
          loading={llmLoading}
          suggestions={llmSuggestions}
          replacementInput={replacementInput}
        />
      )}
      <Box className={classes.footer}>
        <Typography variant="pXs" color={palette.gray_300}>
          {tab === 'oxford' ? `Powered by Oxford Languages` : `Powered by AI`}
        </Typography>
      </Box>
    </Box>
  );
};

export default TabbedReplacementsBody;
